// components/auth/PasswordUpdateForm.jsx
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import authServices from '../../services/AuthServices';

function PasswordUpdateForm() {
    const [passwordType, setPasswordType] = useState('password');
    const [passMatch, setPassMatch] = useState(true);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const oldPassRef = useRef();
    const newPassRef = useRef();
    const confirmRef = useRef();

    const navigate = useNavigate();

    const togglePassword = () => {
        setPasswordType(prev => prev === 'password' ? 'text' : 'password');
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        setError(null);

        const oldPass = oldPassRef.current?.value;
        const newPass = newPassRef.current?.value;
        const confirmPass = confirmRef.current?.value;

        // Validation
        if (!oldPass || !newPass || !confirmPass) {
            toast.error('All fields are required.');
            return;
        }

        if (newPass !== confirmPass) {
            setPassMatch(false);
            toast.error('Passwords do not match.');
            return;
        }

        if (newPass.length < 8) {
            toast.error('Password must be at least 8 characters.');
            return;
        }

        setPassMatch(true);
        setLoading(true);

        const updateToast = toast.loading('Updating password...');

        try {
            const formData = new FormData();
            formData.append('old_password', oldPass);
            formData.append('password', newPass); 
            formData.append('password2', confirmPass);

            await authServices.changePassword(formData);

            // Clear fields
            if (oldPassRef.current) oldPassRef.current.value = '';
            if (newPassRef.current) newPassRef.current.value = '';
            if (confirmRef.current) confirmRef.current.value = '';

            toast.success('Password updated successfully!', { id: updateToast });
            navigate('/auth/profile'); 

        } catch (err) {
            console.error('Password update error:', err);

            let errorMessage = 'Failed to update password';

            if (err.response?.data?.details) {
                const messages = err.response.data.details
                    .map(d => d.message)
                    .filter(Boolean)
                    .join('; ') || 'Validation failed';
                errorMessage = messages;
            } else if (err.response?.data?.detail || err.response?.data?.message) {
                errorMessage = err.response.data.detail || err.response.data.message;
            } else if (err.response?.status === 400) {
                errorMessage = 'Invalid old password or weak new password';
            } else if (err.response?.status === 401) {
                errorMessage = 'Session expired. Please log in again.';
            } else if (err.request) {
                errorMessage = 'Network error — please check your connection';
            }

            setError(errorMessage);
            toast.error(errorMessage, { id: updateToast });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex justify-center">
            <div className="w-full md:w-10/12 lg:w-8/12">
                <div className="bg-white dark:bg-slate-900 p-8 border rounded-lg shadow-xl">
                    <div className="mb-8">
                        <h2 className="text-3xl font-bold text-primary dark:text-secondary">
                            Change Password
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mt-2">
                            Update your login credentials.
                        </p>
                    </div>

                    <form className="space-y-6" onSubmit={submitHandler}>
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                                {error}
                            </div>
                        )}

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Old Password: *</span>
                            </label>
                            <input
                                type={passwordType}
                                required
                                ref={oldPassRef}
                                name="old_password"
                                className="input input-bordered w-full border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">New Password: *</span>
                            </label>
                            <input
                                type={passwordType}
                                required
                                ref={newPassRef}
                                name="new_password"
                                className="input input-bordered w-full border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Confirm: *</span>
                            </label>
                            <input
                                type={passwordType}
                                required
                                ref={confirmRef}
                                name="confirm_password"
                                className="input input-bordered w-full border p-2 rounded-md border-border-light dark:border-border-dark bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                            />
                            {!passMatch && (
                                <div className="label">
                                    <span className="label-text text-red-500">Passwords do not match</span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-secondary"
                                id="show-password"
                                onClick={togglePassword}
                            />
                            <label className="label ml-2 cursor-pointer" htmlFor="show-password">
                                <span className="label-text">Show Password</span>
                            </label>
                        </div>

                        <div>
                            <button
                                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? 'Updating...' : 'Update Password'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default PasswordUpdateForm;